import * as React from 'react';

import "./address-search-section.scss";

import {cx} from 'emotion';

import {cs}                 from "@Common/react/chain-services";
import {UseState}           from "@Common/react/use-state";
import {Form2}              from "@Common/react/cs-form-2/form2";
import {required}           from "@Common/react/cs-form-2/validators/required";
import {bindInput}          from "@Common/react/bind-input";
import {NumberInputBinding} from "@Common/react/number-input-binding";
import {snv}                from "@Common/utils/arr-path";

import {InformationBadge} from "../../../../../../../common/design2/information-badge/information-badge";
import {CheckboxLine}     from "../../../../demographics/step-2-customize/form/checkbox-line/checkbox-line";
import {RadioOption}      from "../../../../common/radio-option/radio-option";
import {SearchError}      from "../../../step-1-search/searching-section/search-error/search-error";

export const AddressSearchSection = ({onSearch, next}) => cs(
    ["data", (_, next) => UseState({
        initValue: {addressType: "both"},
        next
    })],
    ["searching", (_, next) => UseState({next})],
    ["form", ({data, searching}, next) => Form2({
        data,
        fields: {
            "address"       : [required],
            "zip"           : [required],
            "addressType"   : [required],
            "quantity"      : [
                required,
                {
                    name: "quantity",
                    validate: (quantity) => quantity > 0,
                }
            ],
        },
        onSubmit: async () => {
            searching.onChange(true);
            await onSearch({data});
            searching.onChange(false);
            return false
        },
        next,
    })],
    ({data, form, searching}) => next({
        target: data.value,
        render: ({searchError}) => (
            <div className="address-search-section-4z4">
                <div className="header">
                    <div
                        className="title"
                        xd-qa-name="Build Radius List Target Area"
                        xd-qa-type="label"
                    >
                        Target Area
                    </div>
                    <div
                        className="sub-title"
                        xd-qa-name="Build Radius List Target Area Description"
                        xd-qa-type="label"
                    >
                        Enter the address you wish to center your list around
                    </div>
                </div>
                <div className="form">
                    <div className="line address">
                        <div className="label" xd-qa-type="label" xd-qa-name="Build Radius List Property Address">
                            Property Address
                        </div>

                        {cs(
                            ["focus", (_, next) => UseState({next})],
                            ({focus}) => {
                                const field = form.field(["address"]);
                                return (
                                    <div {...{
                                        className: cx("control", {
                                            focus: focus.value,
                                            hasError: field.error,
                                        }),
                                    }}
                                    >
                                        <input {...{
                                            placeholder: "123 Some Street",
                                            onFocus: () => focus.onChange(true),
                                            onBlur: () => focus.onChange(false),
                                            ...bindInput(field.state),
                                            "xd-qa-type": "text_field",
                                            "xd-qa-name": "Build Radius List Property Address",
                                        }} />

                                        {InformationBadge({
                                            getTooltip: () => (
                                                <ul className="message-5ww">
                                                    <li>Include the street address you wish to center your list around.</li>
                                                    <li>Omit periods following abbreviations.</li>
                                                    <li>Abbreviate the cardinal direction in a street name (i.e. S for South. NW for Northwest)</li>
                                                </ul>
                                            ),
                                            xdQaName: "Build Radius List Property Address",
                                        })}
                                    </div>
                                );
                            }
                        )}
                    </div>
                    <div className="line zip">
                        <div className="label" xd-qa-type="label" xd-qa-name="Build Radius List Zip Code">
                            Zip Code
                        </div>

                        {(() => {

                            const field = form.field(["zip"]);
                            return (
                                <div className="control">
                                    <input {...{
                                        placeholder: "90210",
                                        className: cx({hasError: field.error}),
                                        ...bindInput(field.state),
                                        maxLength: 5,
                                        "xd-qa-type": "text_field",
                                        "xd-qa-name": "Build Radius List Zip Code",
                                    }}/>
                                </div>
                            );
                        })()}
                    </div>
                    <div className="line qty">
                        <div className="label" xd-qa-type="label" xd-qa-name="Build Radius List QTY">
                            Quantity
                        </div>

                        {cs(
                            ["field", (_, next) => next(form.field(["quantity"]))],
                            ["focus", (_, next) => UseState({next})],
                            ["binding", ({field}, next) => NumberInputBinding({
                                state: field.state,
                                next,
                            })],
                            ({focus, binding, field}) => (
                                <div className={cx("control", {focus: focus.value, hasError: field.error})}>
                                    <input {...{
                                        ...binding,

                                        placeholder: "25",
                                        onFocus: () => focus.onChange(true),
                                        onBlur: () => {
                                            binding.onBlur();
                                            focus.onChange(false);
                                        },
                                    }}
                                            xd-qa-type="text_field"
                                            xd-qa-name="Build Radius List QTY"
                                    />

                                    {InformationBadge({
                                        getTooltip: () => "Enter the number of addresses you want included in this list. Please note that the quantity adjusts the cost of your list.",
                                        xdQaName: "Build Radius List QTY",
                                    })}
                                </div>
                            )
                        )}
                    </div>
                    <div className="line">
                        <div className="label" xd-qa-type="label" xd-qa-name="Build Radius List Address Type">
                            Address Type
                        </div>

                        {(() => {
                            const field = form.field(["addressType"]);
                            return (
                                <div className="control">
                                    {RadioOption({
                                        label: "Single and Multi-Family",
                                        selected: field.state.value === "both",
                                        onSelect: () => field.state.onChange("both"),
                                        style: {color: field.error && "danger"},
                                        xdQaName: "Build Radius List Single and Multi-Family"
                                    })}
                                    {RadioOption({
                                        label: "Single Family Only",
                                        selected: field.state.value === "single",
                                        onSelect: () => field.state.onChange("single"),
                                        style: {color: field.error && "danger"},
                                        xdQaName: "Build Radius List Single Family Only"
                                    })}
                                    {RadioOption({
                                        label: "Multi-Family Only",
                                        selected: field.state.value === "multi",
                                        onSelect: () => field.state.onChange("multi"),
                                        style: {color: field.error && "danger"},
                                        xdQaName: "Build Radius List Multi-Family Only"
                                    })}
                                </div>
                            );
                        })()}
                    </div>
                    <div className="line exclude">
                        <div className="label spacer" />
                        <div className="control">
                            {CheckboxLine({
                                state: form.field(["excludeTargetAddress"]).state,
                                text: "Exclude target address from search result",
                                xdQaName: "Exclude Target Address",
                            })}
                            {InformationBadge({
                                getTooltip: () => "Provide house number and apartment number with property address to avoid excluding multiple addresses within the target building.",
                                xdQaName: "Excude Target Address Tooltip",
                            })}
                        </div>
                    </div>

                </div>
                
                {searchError.value && (
                    SearchError({
                        error: searchError.value,
                        onChangeStreet: async (street) => {
                            searchError.onChange(null);
                            const newForm = snv(data.value, ["address"], street);
                            data.onChange(newForm);
                            searching.onChange(true);
                            await onSearch({
                                data: {
                                    value: newForm,
                                    onChange: data.onChange,
                                },
                            });
                            searching.onChange(false);
                        },
                    })
                )}
                
                <div className="footer">
                    <a {...{
                        className: "reset",
                        onClick: () => data.onChange({addressType: "both"}),
                        "xd-qa-type": "button",
                        "xd-qa-name": "Build Radius List Reset",
                    }}>
                        Reset
                    </a>
                    <button {...{
                        className: cx("search", {disabled: !form.valid}),
                        disabled: searching.value,
                        onClick: form.submit,
                        "xd-qa-type": "button",
                        "xd-qa-name": "Build Radius List Search",
                    }}>
                        {searching.value ? "Searching..." : "View Results"}
                    </button>
                </div>
            </div>
        )
    })
);